// The app tabs a page adds to its own tab bar. A tab is a route the page owns, so
// the page draws the bar and the routes, and this hook only says which tabs
// exist, in which order, and under which label. The frame behind a tab is
// `AppTabPanel`'s.

import type { SlotId } from '@luminovo/apps-types';
import { useMemo } from 'react';

import { compareWidgetKeys } from '../routing/matchFullPageAssignment';
import { type SlotEmbed, useSlotEmbeds } from './useSlotEmbeds';

/** One app tab, ready for the page's tab bar. */
export interface AppTab {
    /** `${app.id}:${widget.id}`. Stable across renders; unique within the slot. */
    key: string;
    appId: string;
    widgetId: string;
    /** The widget name for the locale, with ` (<app name>)` added when another tab has the same label. */
    label: string;
}

const tabOrder = (embed: SlotEmbed): number | undefined =>
    embed.assignment.widget.kind === 'tab' ? embed.assignment.widget.order : undefined;

/**
 * The app tabs a tab slot shows: every usable widget, sorted by its `order`
 * (a tab without one after every tab with one), then its label, then
 * `${app.id}:${widget.id}`.
 *
 * Two tabs with the same label both get their app's name, so neither one looks
 * like the product's. Empty while the apps session is being minted, so the tabs
 * appear at once rather than one by one.
 */
export function useAppTabs(
    slotId: SlotId,
    params: Readonly<Record<string, unknown>>,
    locale: string,
): readonly AppTab[] {
    const embeds = useSlotEmbeds(slotId, params, locale);

    return useMemo(() => {
        const sorted = [...embeds].sort((a, b) => {
            const orderA = tabOrder(a);
            const orderB = tabOrder(b);
            if (orderA !== orderB) {
                if (orderA === undefined) return 1;
                if (orderB === undefined) return -1;
                return orderA - orderB;
            }
            return a.name.localeCompare(b.name, locale) || compareWidgetKeys(a.key, b.key);
        });
        const labelCounts = new Map<string, number>();
        for (const { name } of sorted) labelCounts.set(name, (labelCounts.get(name) ?? 0) + 1);

        return sorted.map((embed) => ({
            key: embed.key,
            appId: embed.assignment.app.id,
            widgetId: embed.assignment.widget.id,
            label: labelCounts.get(embed.name)! > 1 ? `${embed.name} (${embed.assignment.app.name})` : embed.name,
        }));
    }, [embeds, locale]);
}
